/**
 * Tiny helper component used as the default `SimplAuthGuard` unauthed
 * fallback once the Amplify `<Authenticator>` UI is removed (Commit L
 * of the simpl_auth migration plan).
 *
 * Behaviour: on mount, kick the browser over to simpl_auth's sign-in
 * flow via `simplAuthSignInUrl()`. Renders a small "Redirecting…"
 * splash that the user typically sees only as a flicker before the
 * navigation completes.
 *
 * We deliberately use `window.location.assign` (not React Router's
 * `<Navigate>`) because simpl_auth lives at a different host
 * (`auth[-env].simpl.org` in deployed envs, `localhost:8088/authorize`
 * proxied in dev). We're leaving the SPA entirely until simpl_auth
 * redirects back with a `SIMPL_AUTH_JWT_<env>` cookie set on the
 * shared parent domain.
 */

import { useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

import { simplAuthSignInUrl } from "./simplAuthSession";

type SignInRedirectProps = {
  /**
   * URL to return to after sign-in completes. Defaults to the current
   * `window.location.href` so the user lands back on the page they
   * tried to reach. simpl_auth validates this against its own
   * `*.simpl.org`/dev-localhost allow-list, so a hostile referrer
   * can't steer the post-sign-in landing.
   */
  returnTo?: string;
};

export const SignInRedirect = ({ returnTo }: SignInRedirectProps) => {
  useEffect(() => {
    const url = simplAuthSignInUrl(returnTo);
    window.location.assign(url);
  }, [returnTo]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: "100vh"
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Redirecting to sign in…
      </Typography>
    </Box>
  );
};
